import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { formatCentralDateTime } from "@/lib/fieldRoutesDate"

type RelatedSummary = { id: number; name: string; company_name: string | null; phone: string | null; email: string | null; field_routes_id: string | null }

type RelatedLead = { id: number; stage: { name: string }; fieldroutes_lead_id: string | null; fieldroutes_subscription_id: string | null }

type WebhookEvent = {
  id: number
  endpoint: string
  event_type: string
  http_method: string
  result_status: string
  response_status: number | null
  received_at: string
  customer_id: number | null
  lead_id: number | null
  fieldroutes_customer_id: string | null
  fieldroutes_customer_number: string | null
  fieldroutes_lead_id: string | null
  fieldroutes_subscription_id: string | null
  fieldroutes_lead_stage_id: number | null
  fieldroutes_lead_stage: string | null
  fieldroutes_lead_status_text: string | null
  fieldroutes_active: string | null
  action_taken: string | null
  summary: string | null
  error_message: string | null
  error_backtrace_summary: string | null
  fieldroutes_enrichment_status: string | null
  fieldroutes_enrichment_error: string | null
  fieldroutes_enriched_at: string | null
  original_webhook_event_id: string | null
  original_webhook_idempotency_key: string | null
  request_headers_summary: Record<string, unknown>
  raw_payload: Record<string, unknown>
  customer: RelatedSummary | null
  lead: RelatedLead | null
}

export default function AdminFieldRoutesWebhooksShow({ event }: { event: WebhookEvent }) {
  return (
    <>
      <Head title={`Webhook Event #${event.id} | LeadFlow`}>
        <meta name="description" content="Inspect a FieldRoutes webhook event." />
        <meta property="og:title" content={`Webhook Event #${event.id} | LeadFlow`} />
        <meta property="og:description" content="Inspect a FieldRoutes webhook event." />
      </Head>
      <AdminShell wide>
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-hairline pb-6">
          <div>
            <h1>Webhook Event #{event.id}</h1>
            <p className="mt-1 text-sm text-ink-muted">{event.endpoint} · {event.http_method} · {formatCentralDateTime(event.received_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={event.result_status === "error" || event.result_status === "rejected" ? "signal" : "accent"}>{event.result_status}</Badge>
            {event.response_status !== null && <Badge tone="neutral">HTTP {event.response_status}</Badge>}
            <Link href="/admin/field-routes-webhooks" className="text-sm text-ink-muted no-underline hover:text-ink-display">Back to logs</Link>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Panel title="Summary">
            <Detail label="Action taken" value={event.action_taken || event.summary || "—"} />
            <Detail label="Endpoint" value={event.endpoint} />
            <Detail label="HTTP method" value={event.http_method} />
            <Detail label="Result status" value={event.result_status} />
            <Detail label="Response status" value={event.response_status?.toString() || "—"} />
            <Detail label="Matched customer" value={linkedCustomer(event)} />
            <Detail label="Matched lead" value={linkedLead(event)} />
            <Detail label="FieldRoutes customer ID" value={event.fieldroutes_customer_id || "—"} />
            <Detail label="FieldRoutes lead ID" value={event.fieldroutes_lead_id || "—"} />
            <Detail label="Subscription ID" value={event.fieldroutes_subscription_id || "—"} />
            <Detail label="Enrichment status" value={event.fieldroutes_enrichment_status || "—"} />
            <Detail label="Enrichment error" value={event.fieldroutes_enrichment_error || "—"} />
            <Detail label="Enriched at" value={event.fieldroutes_enriched_at ? formatCentralDateTime(event.fieldroutes_enriched_at) : "—"} />
          </Panel>


          <Panel title="Original LeadFlow routing trace">
            <Detail label="Original webhook event ID" value={event.original_webhook_event_id || "—"} />
            <Detail label="Original idempotency key" value={event.original_webhook_idempotency_key || "—"} />
          </Panel>
\n          <Panel title="Normalized Fields">
            <Detail label="customerID" value={event.fieldroutes_customer_id || "—"} />
            <Detail label="customerNumber" value={event.fieldroutes_customer_number || "—"} />
            <Detail label="leadID" value={event.fieldroutes_lead_id || "—"} />
            <Detail label="subscriptionID" value={event.fieldroutes_subscription_id || "—"} />
            <Detail label="leadStageID" value={event.fieldroutes_lead_stage_id?.toString() || "—"} />
            <Detail label="leadStage" value={event.fieldroutes_lead_stage || "—"} />
            <Detail label="leadStatusText" value={event.fieldroutes_lead_status_text || "—"} />
            <Detail label="active" value={event.fieldroutes_active || "—"} />
          </Panel>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Panel title="Headers summary">
            <JsonBlock value={event.request_headers_summary} />
          </Panel>
          <Panel title="Raw payload">
            <JsonBlock value={event.raw_payload} />
          </Panel>
        </div>

        {(event.error_message || event.error_backtrace_summary) && (
          <Panel className="mt-6" title="Error details">
            <Detail label="Error message" value={event.error_message || "—"} />
            <pre className="mt-4 overflow-auto rounded-md border border-hairline bg-page p-4 text-xs text-ink-body">{event.error_backtrace_summary || "No backtrace captured."}</pre>
          </Panel>
        )}
      </AdminShell>
    </>
  )
}

function Panel({ title, children, className = "" }: { title: string; children: React.ReactNode; className?: string }) {
  return <section className={`rounded-lg border border-hairline bg-surface p-5 ${className}`}><h2 className="text-lg font-semibold text-ink-display">{title}</h2><div className="mt-4 space-y-3">{children}</div></section>
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return <div><div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div><div className="mt-1 break-words text-sm text-ink-body">{value}</div></div>
}

function JsonBlock({ value }: { value: Record<string, unknown> }) {
  return <pre className="overflow-auto rounded-md border border-hairline bg-page p-4 text-xs text-ink-body">{JSON.stringify(value, null, 2)}</pre>
}

function linkedCustomer(event: WebhookEvent) {
  if (!event.customer) return "—"
  return <Link href={`/customers/${event.customer.id}`}>{event.customer.name}</Link>
}

function linkedLead(event: WebhookEvent) {
  if (!event.lead) return "—"
  return <Link href={`/dashboard?lead_id=${event.lead.id}`}>Lead #{event.lead.id}</Link>
}

